/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useContext } from 'react';
import {
  ActorRefFrom,
  assign,
  createMachine,
  MachineConfig,
  MachineOptions,
} from 'xstate';
import { useActor } from '@xstate/react';

import {
  CommunityResultQuery,
  EmptyObject,
  TissueNetwork,
} from '../shared/sharedTypes';
import { getCommunityBodyTag } from '../community/community_util';
import { withExtendedUniverse } from '../../core/networkUtils';
import { pollCommunityResult } from './pollCommunity';
import {
  BASE_URI,
  HB_API_VERSION,
  NETWORK_VERSIONS,
  TISSUE_DB,
  VERSION_IDENTIFIERS,
} from '../../settings';
import ApplicationContext from '../ApplicationContext';
import {
  areEntrezListsEqual,
  baseSlug,
  globalSlugFor,
  resolveSlug,
} from '../../core/util';

// Loose enrichment shape — every termDB slug lookup yields a list
// of enrichment rows. The row contents are touched by many
// downstream views with their own narrowing, so we don't pin them
// here.
export interface CommunityResult {
  query?: CommunityResultQuery;
  edges?: unknown[];
  enrichment?: Record<string, unknown[] | undefined>;
  [extra: string]: unknown;
}

export type FMDActor = ActorRefFrom<typeof fmdMachine>;

// Utility hooks
export const useFMD = () => {
  const { xstate } = useContext(ApplicationContext);
  const { fmdService } = xstate;
  return useActor(fmdService as FMDActor);
};

const filterTissueIntegrations = (
  integrations: TissueNetwork[],
  versionId: string,
) => {
  if (!integrations) return [];
  return integrations.filter(integration => {
    if (versionId === VERSION_IDENTIFIERS[NETWORK_VERSIONS.GIANT]) {
      // V1: global (no suffix) + BRENDA tissue integrations
      if (integration.slug === 'global') return true;
      return integration?.context?.term?.database?.slug === TISSUE_DB;
    }
    // V3 (MAGE): Filter by version identifier (includes global-v3)
    return integration?.context?.version?.identifier === versionId;
  });
};

export interface FMDMachineContext {
  // Aborts the in-flight community poll when the fetchingCommunity
  // state exits (e.g. a new submission, success, or error). Does NOT
  // fire on browser navigation — fmdService is a singleton at the
  // App root (App.js), so route changes don't transition the machine
  // and polling continues until task completion or 20min timeout.
  abortController: AbortController | null;
  bodyTag: string | null;
  communityResult: CommunityResult | null;
  entrezList: string[];
  extendedUniverse: boolean;
  giantVersion: string;
  hasError: boolean;
  integrations: TissueNetwork[];
  selectedNetworkSlug: string | null;
  selectedTissue: string | null;
  selectedTermDbSlug: string | null;
  title: string | null;
}

type FetchCommunityNetworksDoneEvent = {
  type: 'done.invoke.fmd.networksManager.fetchingNetworks:invocation[0]';
  data: {
    integrations: TissueNetwork[];
  };
};

type FetchCommunityDoneEvent = {
  type: 'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]';
  data: {
    communityResult: CommunityResult;
  };
};

export type FMDMachineEvent =
  | { type: 'RESET' }
  | {
      type: 'FETCH_COMMUNITY';
      bodyTag?: string;
      entrezList?: string[];
      giantVersion?: string;
      selectedNetworkSlug?: string | null;
      title?: string;
    }
  | {
      type: 'SELECT_GIANT_VERSION';
      giantVersion: string;
    }
  | {
      type: 'UPDATE_ENTREZ_LIST';
      entrezList: string[];
      selectedTissue?: string;
    }
  | {
      type: 'UPDATE_SELECTED_NETWORK';
      selectedNetworkSlug: string | null;
      selectedTissue?: string;
    }
  | {
      type: 'UPDATE_SELECTED_TERM_DB';
      selectedTermDbSlug: string;
    }
  | {
      type: 'UPDATE_TITLE';
      title: string;
    }
  | FetchCommunityNetworksDoneEvent
  | FetchCommunityDoneEvent;

export interface FMDStateSchema {
  states: {
    enrichmentDbManager: {
      states: {
        idle: EmptyObject;
      };
    };
    networksManager: {
      states: {
        idle: EmptyObject;
        fetchingNetworks: EmptyObject;
        hasNetworks: EmptyObject;
      };
    };
    communityQueryManager: {
      states: {
        idle: EmptyObject;
        fetchingCommunity: EmptyObject;
        hasCommunity: EmptyObject;
      };
    };
  };
}

const defaultContext = {
  abortController: null,
  bodyTag: null,
  communityResult: null,
  entrezList: [],
  extendedUniverse:
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('extended_universe') ===
      'true',
  giantVersion: NETWORK_VERSIONS.GIANT,
  hasError: false,
  integrations: [],
  selectedNetworkSlug: null,
  selectedTissue: null,
  selectedTermDbSlug: null,
  title: null,
};
const reset = {
  target: 'idle',
  actions: ['resetContext'],
};

export const fmdMachineConfig: MachineConfig<
  FMDMachineContext,
  FMDStateSchema,
  FMDMachineEvent
> = {
  schema: {
    context: {} as FMDMachineContext,
    events: {} as FMDMachineEvent,
  },
  id: 'fmd',
  type: 'parallel',
  context: defaultContext,
  states: {
    networksManager: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            RESET: reset,
            // Fetch networks in case we are loading from api cache/browser refresh
            'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]': {
              target: 'fetchingNetworks',
            },
            UPDATE_ENTREZ_LIST: [
              {
                cond: 'hasEntrezListChanged',
                actions: ['assignEntrezList', 'assignBodyTag'],
                target: 'fetchingNetworks',
              },
              {
                cond: 'needsNetworks',
                actions: ['assignEntrezList', 'assignBodyTag'],
                target: 'fetchingNetworks',
              },
            ],
            SELECT_GIANT_VERSION: {
              actions: ['assignGiantVersion'],
              target: 'fetchingNetworks',
            },
          },
        },
        fetchingNetworks: {
          entry: ['clearError'],
          on: {
            RESET: reset,
            UPDATE_ENTREZ_LIST: [
              {
                cond: 'hasEntrezListChanged',
                actions: ['assignEntrezList', 'assignBodyTag'],
                target: 'fetchingNetworks',
              },
            ],
            SELECT_GIANT_VERSION: {
              actions: ['assignGiantVersion'],
              target: 'fetchingNetworks',
            },
          },
          invoke: {
            src: 'fetchCommunityNetworks',
            onDone: {
              actions: [
                'assignIntegrations',
                'assignSelectedNetworkSlug',
                'assignBodyTag',
              ],
              target: 'hasNetworks',
            },
            onError: {
              actions: 'setError',
              target: 'idle',
            },
          },
        },
        hasNetworks: {
          on: {
            RESET: reset,
            UPDATE_ENTREZ_LIST: [
              {
                cond: 'hasEntrezListChanged',
                actions: ['assignEntrezList', 'assignBodyTag'],
                target: 'fetchingNetworks',
              },
            ],
            SELECT_GIANT_VERSION: {
              actions: ['assignGiantVersion'],
              target: 'fetchingNetworks',
            },
          },
        },
      },
    },
    enrichmentDbManager: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            RESET: reset,
            UPDATE_SELECTED_TERM_DB: {
              actions: ['assignSelectedTermDbSlug'],
              target: 'idle',
            },
          },
        },
      },
    },
    communityQueryManager: {
      initial: 'idle',
      states: {
        idle: {
          always: [
            {
              // If we have the same integration and entrez list as the communityResult query,
              //  we have the community data
              cond: (context: FMDMachineContext): boolean => {
                const {
                  entrezList,
                  giantVersion,
                  selectedNetworkSlug,
                  communityResult,
                  title,
                } = context;
                const query = communityResult?.query;
                const queryIntegrationSlug = query?.integration;
                const queryEntrezList = query?.entrez_list;
                const isSameGiantVersion =
                  giantVersion === query?.giant_version;
                const isSameTitle = title === (query?.title ? query.title : '');
                const isSameQuerySlug =
                  queryIntegrationSlug === selectedNetworkSlug;
                const isSameEntrezList =
                  entrezList && queryEntrezList
                    ? areEntrezListsEqual(entrezList, queryEntrezList)
                    : false;
                return (
                  isSameQuerySlug &&
                  isSameEntrezList &&
                  isSameTitle &&
                  isSameGiantVersion
                );
              },
              target: 'hasCommunity',
            },
          ],
          on: {
            FETCH_COMMUNITY: [
              {
                cond: 'fetchHasEntrezAndTissueNetwork',
                actions: [
                  'assignBodyTag',
                  'assignSelectedNetworkSlug',
                  'assignSelectedTermDbSlug',
                ],
                target: 'fetchingCommunity',
              },
              {
                target: 'fetchingCommunity',
                actions: ['assignBodyTag'],
              },
            ],
            UPDATE_SELECTED_NETWORK: {
              actions: ['assignSelectedNetworkSlug', 'assignBodyTag'],
              target: 'idle',
            },
            UPDATE_TITLE: {
              actions: ['assignTitle', 'assignBodyTag'],
              target: 'idle',
            },
          },
        },
        fetchingCommunity: {
          entry: ['clearError', 'createAbortController'],
          exit: ['abortCommunityPoll'],
          on: {
            RESET: reset,
            FETCH_COMMUNITY: {
              target: 'fetchingCommunity',
              actions: [
                'assignBodyTag',
                'assignSelectedNetworkSlug',
                'assignSelectedTermDbSlug',
              ],
            },
            // SEEK UI makes UPDATE_ENTREZ_LIST relevant here
            UPDATE_ENTREZ_LIST: {
              target: 'idle',
            },
            // SEEK UI makes SELECT_GIANT_VERSION relevant here
            SELECT_GIANT_VERSION: {
              target: 'idle',
            },
            UPDATE_SELECTED_NETWORK: {
              actions: ['assignSelectedNetworkSlug', 'assignBodyTag'],
              target: 'idle',
            },
            UPDATE_TITLE: {
              actions: ['assignTitle', 'assignBodyTag'],
              target: 'idle',
            },
          },
          invoke: {
            src: 'fetchCommunity',
            onDone: {
              actions: [
                'assignCommunity',
                'assignEntrezList',
                'assignGiantVersion',
                'assignSelectedNetworkSlug',
                'assignSelectedTermDbSlug',
                'assignTitle',
              ],
              target: 'hasCommunity',
            },
            onError: {
              actions: 'setError',
              target: 'idle',
            },
          },
        },
        hasCommunity: {
          on: {
            RESET: reset,
            FETCH_COMMUNITY: {
              target: 'fetchingCommunity',
              actions: ['assignBodyTag'],
            },
            // SEEK UI makes UPDATE_ENTREZ_LIST relevant here
            UPDATE_ENTREZ_LIST: {
              target: 'idle',
            },
            // SEEK UI makes SELECT_GIANT_VERSION relevant here
            SELECT_GIANT_VERSION: {
              target: 'idle',
            },
            UPDATE_SELECTED_NETWORK: {
              actions: ['assignSelectedNetworkSlug', 'assignBodyTag'],
              target: 'idle',
            },
            UPDATE_TITLE: {
              actions: ['assignTitle', 'assignBodyTag'],
              target: 'idle',
            },
          },
        },
      },
    },
  },
};

export const fmdMachineOptions: MachineOptions<
  FMDMachineContext,
  FMDMachineEvent
> = {
  activities: {},
  delays: {},
  guards: {
    fetchHasEntrezAndTissueNetwork: (_, event) => {
      if (event.type === 'FETCH_COMMUNITY') {
        return !!(event.entrezList && event.selectedNetworkSlug);
      }
      return false;
    },
    hasEntrezListChanged: (context, event) => {
      if (event.type === 'UPDATE_ENTREZ_LIST') {
        return (
          JSON.stringify(context.entrezList.sort()) !==
          JSON.stringify(event.entrezList.sort())
        );
      }
      return false;
    },
    needsNetworks: (context, event) => {
      if (event.type === 'UPDATE_ENTREZ_LIST') {
        return (
          // We have entrez, but no integrations or networks
          context.integrations.length === 0 &&
          (context.entrezList.length > 0 || event.entrezList.length > 0)
        );
      }
      return false;
    },
  },
  services: {
    fetchCommunityNetworks: async () => {
      const integrations = await fetch(
        `${BASE_URI}integrations/?_=${HB_API_VERSION}`,
      ).then(res => res.json());

      return { integrations };
    },
    fetchCommunity: async (
      context: FMDMachineContext,
      event: FMDMachineEvent,
    ) => {
      const {
        bodyTag,
        entrezList,
        giantVersion,
        selectedNetworkSlug,
        title,
        communityResult: oldCommunityResult,
      } = context;

      if (event.type !== 'FETCH_COMMUNITY') return null;

      let communityApiUrl = '';
      let body: string | undefined;
      let tmpBodyTag = bodyTag;

      if (event.bodyTag) {
        // Body_tag-only refresh: backend keys cache solely on
        // body_tag (the SHA already encodes extended_universe via
        // getCommunityBodyTag). The URL extended_universe param has
        // no role on cache lookup; we don't send it. The frontend
        // uses it only as a UI signal — see CommunityPage's banner
        // that compares URL extended_universe against
        // result.query.extended_universe.
        tmpBodyTag = event.bodyTag;
        communityApiUrl = `${BASE_URI}integrations/community/?body_tag=${tmpBodyTag}`;
      } else if (giantVersion && tmpBodyTag) {
        // Is a new query
        communityApiUrl = withExtendedUniverse(
          `${BASE_URI}integrations/community/?integration=${selectedNetworkSlug}&giant_version=${giantVersion}&body_tag=${tmpBodyTag}${
            title?.length ? `&title=${encodeURIComponent(title)}` : ''
          }`,
          context.extendedUniverse,
        );
        body = JSON.stringify({ entrez: entrezList });
      } else {
        // Is an old GIANT-v1 query before v2 was available, needs updating
        const { query } = oldCommunityResult;
        const {
          title: cachedTitle,
          entrez_list: cachedEntrezList,
          integration: cachedIntegrationSlug,
        } = query;

        const updatedIntegrationSlug = `${cachedIntegrationSlug}-v1`;
        const entrezListFormatted = cachedEntrezList.map((entrez: string) => ({
          entrez,
        }));
        // The latest `bodyTag` relies on the GIANT version
        tmpBodyTag = getCommunityBodyTag(
          entrezListFormatted,
          updatedIntegrationSlug,
          NETWORK_VERSIONS.GIANT,
          cachedTitle || '',
          context.extendedUniverse,
        );

        communityApiUrl = withExtendedUniverse(
          `${BASE_URI}integrations/community/?integration=${updatedIntegrationSlug}&giant_version=${
            event.giantVersion
          }&body_tag=${tmpBodyTag}${
            cachedTitle?.length
              ? `&title=${encodeURIComponent(cachedTitle)}`
              : ''
          }`,
          context.extendedUniverse,
        );
        body = JSON.stringify({ entrez: cachedEntrezList });
      }

      // AbortController is created by fetchingCommunity's entry action
      // (createAbortController); the exit action aborts on state
      // transitions — NOT on route navigation (singleton machine).
      const communityResult = await pollCommunityResult(
        communityApiUrl,
        body,
        5000,
        240,
        context.abortController?.signal,
      );
      return { communityResult, bodyTag: tmpBodyTag };
    },
  },
  actions: {
    createAbortController: assign({
      abortController: (context: FMDMachineContext) => {
        // Abort any lingering controller (defensive; the exit action
        // on fetchingCommunity normally handles cleanup, but rapid
        // re-entry before exit runs could leave one around).
        if (context.abortController) context.abortController.abort();
        return new AbortController();
      },
    }),
    abortCommunityPoll: (context: FMDMachineContext) => {
      if (context.abortController) {
        context.abortController.abort();
      }
    },
    assignBodyTag: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (event.type === 'FETCH_COMMUNITY' && event.bodyTag)
          return { bodyTag: event.bodyTag };

        let tmpEntrezList = context.entrezList;
        let tmpSelectedNetworkSlug: string | null = context.selectedNetworkSlug;
        let tmpTitle = context?.title || '';
        let tmpGiantVersion = context?.giantVersion || '';

        if (event.type === 'FETCH_COMMUNITY' && event.entrezList) {
          tmpEntrezList = event.entrezList;
          if (event.selectedNetworkSlug) {
            tmpSelectedNetworkSlug = event.selectedNetworkSlug;
          }
          if (event.title) tmpTitle = event.title;
          if (event.giantVersion) tmpGiantVersion = event.giantVersion;
        }

        if (event.type === 'UPDATE_ENTREZ_LIST') {
          const { entrezList } = event;
          tmpEntrezList = entrezList;
        }

        if (event.type === 'UPDATE_SELECTED_NETWORK') {
          const { selectedNetworkSlug } = event;
          tmpSelectedNetworkSlug = selectedNetworkSlug;
        }

        if (event.type === 'UPDATE_TITLE') {
          const { title } = event;
          tmpTitle = title || '';
        }

        if (event.type === 'SELECT_GIANT_VERSION') {
          const { giantVersion } = event;
          tmpGiantVersion = giantVersion || '';
        }

        const bodyTag =
          tmpSelectedNetworkSlug && tmpEntrezList.length > 0
            ? getCommunityBodyTag(
                tmpEntrezList.map(
                  (entrez: string) => ({ entrez }), // format for `getCommunityBodyTag`
                ),
                tmpSelectedNetworkSlug,
                tmpGiantVersion,
                tmpTitle,
                context.extendedUniverse,
              )
            : null;
        return { bodyTag };
      },
    ),
    assignCommunity: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (
          event.type ===
          'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]'
        ) {
          const { communityResult } = event.data;
          return { communityResult };
        }
        throw Error('Wrong event type name :: assignCommunity');
      },
    ),
    assignIntegrations: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (
          event.type ===
          'done.invoke.fmd.networksManager.fetchingNetworks:invocation[0]'
        ) {
          const { giantVersion } = context;
          const versionId = VERSION_IDENTIFIERS[giantVersion];
          const { integrations } = event.data;
          const filteredIntegrations = filterTissueIntegrations(
            integrations,
            versionId,
          );

          // Resolve tissue selection to versioned slug
          const { selectedTissue } = context;
          const globalSlug = globalSlugFor(giantVersion);
          const defaultSlug = selectedTissue
            ? resolveSlug(selectedTissue, giantVersion, filteredIntegrations) ||
              globalSlug
            : globalSlug;

          return {
            ...context,
            integrations: filteredIntegrations,
            selectedNetworkSlug: defaultSlug,
          };
        }
        throw Error('Wrong event type name :: assignIntegrations');
      },
    ),
    assignEntrezList: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (event.type === 'UPDATE_ENTREZ_LIST') {
          const { entrezList, selectedTissue } = event;
          return {
            entrezList,
            integrations: [],
            selectedTissue: selectedTissue || context.selectedTissue,
            selectedNetworkSlug: null,
          };
        }

        if (event.type === 'FETCH_COMMUNITY') {
          const { entrezList } = event;
          return { entrezList };
        }

        if (
          event.type ===
          'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]'
        ) {
          const { communityResult } = event.data;
          const entrezList = communityResult?.query?.entrez_list.map(
            (entrez: string) => Number(entrez),
          );
          return { entrezList };
        }

        return { entrezList: context.entrezList };
      },
    ),
    assignGiantVersion: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (
          event.type ===
          'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]'
        ) {
          const { communityResult } = event.data;
          // If the field is missing from query, must be old result, default to v1
          const giantVersion =
            communityResult?.query?.giant_version || NETWORK_VERSIONS.GIANT;
          return { giantVersion };
        }
        if (event.type === 'SELECT_GIANT_VERSION') {
          const { giantVersion } = event;
          return { giantVersion, selectedNetworkSlug: null };
        }
        return {
          giantVersion: context.giantVersion,
          selectedNetworkSlug: null,
        };
      },
    ),
    assignSelectedNetworkSlug: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (event.type === 'UPDATE_SELECTED_NETWORK') {
          const { selectedNetworkSlug, selectedTissue } = event;
          return {
            selectedNetworkSlug,
            selectedTissue: selectedTissue || baseSlug(selectedNetworkSlug),
          };
        }
        if (event.type === 'FETCH_COMMUNITY' && event.selectedNetworkSlug) {
          const { selectedNetworkSlug } = event;
          return {
            selectedNetworkSlug,
            selectedTissue: baseSlug(selectedNetworkSlug),
          };
        }
        if (
          event.type ===
          'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]'
        ) {
          // In the case where the back / forward buttons fetch a new communityResult
          //  and we need to update selectedNetworkSlug to match once the result is received
          const queryIntegrationSlug =
            event?.data?.communityResult?.query?.integration;
          return {
            selectedNetworkSlug: queryIntegrationSlug,
            selectedTissue: baseSlug(queryIntegrationSlug),
          };
        }

        return { selectedNetworkSlug: context.selectedNetworkSlug };
      },
    ),
    assignSelectedTermDbSlug: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (event.type === 'UPDATE_SELECTED_TERM_DB') {
          const { selectedTermDbSlug } = event;
          return { selectedTermDbSlug };
        }
        if (
          event.type ===
          'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]'
        ) {
          const {
            communityResult: { enrichment },
          } = event.data;

          let maxLength = 0;
          let dbSlugWithMostTerms = 'gene-ontology-bp';
          Object.keys(enrichment || {}).forEach(key => {
            if (enrichment[key]?.length > maxLength) {
              maxLength = enrichment[key].length;
              dbSlugWithMostTerms = key;
            }
          });
          return {
            selectedTermDbSlug:
              // Default is GO unless GO has no enriched terms.
              (enrichment?.['gene-ontology-bp']?.length || 0) > 0
                ? 'gene-ontology-bp'
                : dbSlugWithMostTerms,
          };
        }

        return { selectedNetworkSlug: context.selectedNetworkSlug };
      },
    ),
    assignTitle: assign(
      (context: FMDMachineContext, event: FMDMachineEvent) => {
        if (event.type === 'UPDATE_TITLE') {
          const { title } = event;
          return { title };
        }
        if (event.type === 'FETCH_COMMUNITY' && event.title) {
          const { title } = event;
          return { title };
        }
        if (
          event.type ===
          'done.invoke.fmd.communityQueryManager.fetchingCommunity:invocation[0]'
        ) {
          // In the case where the back / forward buttons fetch a new communityResult
          //  and we need to update title to match once the result is received
          const { communityResult } = event.data;
          const communityResultTitle = communityResult?.query?.title;
          return { title: communityResultTitle || '' };
        }

        return { title: context.title };
      },
    ),
    resetContext: assign(() => {
      // TODO: allow option to preserve specific context keys
      // TODO: test how many times this action runs since there are many parallel machines
      return defaultContext;
    }),
    setError: assign(() => {
      return { hasError: true };
    }),
    clearError: assign(() => {
      return { hasError: false };
    }),
  },
};

const fmdMachine = createMachine(fmdMachineConfig, fmdMachineOptions);
export default fmdMachine;
